import { BALANCE } from '../config/balance';
import { clamp } from '../utils/math';

export interface HeadShift {
  /** Normalised lateral sway request, -1 (left) to 1 (right). */
  x: number;
  /** Normalised vertical dodge request, -1 (down) to 1 (up). */
  y: number;
  /** Normalised forward/back sway request, -1 to 1. */
  z: number;
}

/** Soft symmetric dead zone: zero inside, and a ramp that starts at zero. */
function applyDeadZone(value: number, dead: number): number {
  const magnitude = Math.abs(value);
  if (magnitude <= dead) return 0;
  return Math.sign(value) * (magnitude - dead);
}

export interface HeadShiftOptions {
  /** Dead zone override, metres of viewer offset. */
  deadZone?: number;
  /** Reach override, metres. The screen calibration supplies a real-metre set. */
  maxLateral?: number;
  maxVertical?: number;
  maxForward?: number;
}

/**
 * Map a filtered viewer offset (already scaled by the parallax setting) to a
 * normalised body-sway request.
 *
 * A centred head pose must not move the body, so a dead zone sits around the
 * middle. Past the zone the request ramps up from zero, so a small lean is a
 * small sway and a full lean reaches the bound. The request is normalised and
 * linear in the lean, so `Robot` scales it by the sway range and the body's
 * displacement stays proportional to how far the head has moved from centre.
 */
export function bodyShiftFromOffset(
  viewerX: number,
  viewerForward: number,
  out: HeadShift = { x: 0, y: 0, z: 0 },
  options: HeadShiftOptions = {},
  viewerY = 0,
): HeadShift {
  const off = BALANCE.camera.offAxis;
  const dead = options.deadZone ?? BALANCE.headTracking.bodyShiftDeadZone;
  const maxLateral = options.maxLateral ?? off.maxViewerX;
  const maxVertical = options.maxVertical ?? off.maxViewerY;
  const maxForward = options.maxForward ?? off.maxViewerForward;
  const lateralRange = Math.max(1e-3, maxLateral - dead);
  const forwardRange = Math.max(1e-3, maxForward - dead);
  const verticalRange = Math.max(1e-3, maxVertical - dead);

  const lateral = applyDeadZone(clamp(viewerX, -maxLateral, maxLateral), dead);
  const forward = applyDeadZone(clamp(viewerForward, -maxForward, maxForward), dead);
  const vertical = applyDeadZone(clamp(viewerY, -maxVertical, maxVertical), dead);

  out.x = clamp(lateral / lateralRange, -1, 1);
  out.y = clamp(vertical / verticalRange, -1, 1);
  out.z = clamp(forward / forwardRange, -1, 1) * BALANCE.headTracking.bodyShiftForwardScale;
  return out;
}
